/**
 * v3.tasks.weekly — WEEKLY-TASKS-BUILD client (Bill 2026-04-25)
 *
 * Backed by Functions/WeeklyTasksFunction.cs:
 *   GET   /v3/tasks/weekly?officer=&category=&status=&weekStartDate=&top=
 *   POST  /v3/tasks/weekly/{taskId}/update
 *   POST  /v3/tasks/weekly/{taskId}/complete
 *   POST  /v3/tasks/weekly/{taskId}/expire     (system-only, x-system-caller)
 *   GET   /v3/tasks/weekly/summary
 *
 * Composed onto `TasksApi.weekly` from client.ts after construction
 * (mirrors how AdminApi.cartRetentionPolicies is bolted on — see
 * v3-admin-cart-retention.ts).
 */

import type {
  RequestOptions,
  GenericResult,
  WeeklyTask,
  WeeklyTaskCategory,
  WeeklyTaskCompleteInput,
  WeeklyTaskCriteria,
  WeeklyTaskListFilters,
  WeeklyTaskSummary,
  WeeklyTaskUpdateInput,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

export interface WeeklyTaskCriteriaListResponse {
  success: boolean;
  criteria: ReadonlyArray<WeeklyTaskCriteria>;
}

export interface WeeklyTaskCriteriaUpsertInput {
  taskCategory: WeeklyTaskCategory;
  isActive?: boolean;
  ageThresholdDays?: number | null;
  balanceThresholdAmount?: number | null;
  accountTypeFilter?: string | null;
  notes?: string | null;
  updatedBy?: string;
}

export interface WeeklyTaskCriteriaUpsertResponse {
  success: boolean;
  taskCategory?: string;
  error?: string;
}

export interface WeeklyTasksListResponse {
  success: boolean;
  count: number;
  top?: number;
  tasks: ReadonlyArray<WeeklyTask>;
}

export interface WeeklyTaskWriteResponse {
  success: boolean;
  taskId?: string;
  status?: string;
  error?: string;
  noop?: boolean;
  alreadyCompleted?: boolean;
  alreadyExpired?: boolean;
  completedAt?: string;
}

export interface WeeklyTaskSummaryResponse {
  success: boolean;
  byCategoryStatus: WeeklyTaskSummary['byCategoryStatus'];
  ageBuckets: WeeklyTaskSummary['ageBuckets'];
}

export class WeeklyTasksApi {
  public constructor(private readonly http: ApiHttpClient) {}

  public list(
    filters: WeeklyTaskListFilters = {},
    opts?: RequestOptions,
  ): Promise<WeeklyTasksListResponse> {
    return this.http.get<WeeklyTasksListResponse>('/v3/tasks/weekly', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public update(
    taskId: string,
    body: WeeklyTaskUpdateInput,
    opts?: RequestOptions,
  ): Promise<WeeklyTaskWriteResponse> {
    return this.http.post<WeeklyTaskWriteResponse>(
      `/v3/tasks/weekly/${encodeURIComponent(taskId)}/update`,
      { ...opts, body },
    );
  }

  public complete(
    taskId: string,
    body: WeeklyTaskCompleteInput,
    opts?: RequestOptions,
  ): Promise<WeeklyTaskWriteResponse> {
    return this.http.post<WeeklyTaskWriteResponse>(
      `/v3/tasks/weekly/${encodeURIComponent(taskId)}/complete`,
      { ...opts, body },
    );
  }

  /**
   * System-only: requires `x-system-caller` header. UI should never call this
   * directly; included for completeness so the WeeklyTaskGenerator timer
   * function (or an admin sweeper) can mark expired rows.
   */
  public expire(
    taskId: string,
    systemCaller: string,
    opts?: RequestOptions,
  ): Promise<WeeklyTaskWriteResponse> {
    const headers = { ...(opts?.headers ?? {}), 'x-system-caller': systemCaller };
    return this.http.post<WeeklyTaskWriteResponse>(
      `/v3/tasks/weekly/${encodeURIComponent(taskId)}/expire`,
      { ...opts, headers, body: {} },
    );
  }

  public summary(
    filters: { officer?: string; weekStartDate?: string } = {},
    opts?: RequestOptions,
  ): Promise<WeeklyTaskSummaryResponse> {
    return this.http.get<WeeklyTaskSummaryResponse>('/v3/tasks/weekly/summary', {
      ...opts,
      query: filters as unknown as Record<string, string | number | boolean | undefined>,
    });
  }

  public criteriaList(opts?: RequestOptions): Promise<WeeklyTaskCriteriaListResponse> {
    return this.http.get<WeeklyTaskCriteriaListResponse>('/v3/admin/weekly-task-criteria', { ...opts });
  }

  public criteriaUpsert(
    bypassKey: string,
    body: WeeklyTaskCriteriaUpsertInput,
    opts?: RequestOptions,
  ): Promise<WeeklyTaskCriteriaUpsertResponse> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.post<WeeklyTaskCriteriaUpsertResponse>(
      '/v3/admin/weekly-task-criteria',
      { ...opts, headers, body },
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend TasksApi with `.weekly`.
// ─────────────────────────────────────────────────────────────────────────

declare module './tasks.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface TasksApi {
    /** WEEKLY-TASKS-BUILD — typed weekly task surface. */
    readonly weekly: WeeklyTasksApi;
  }
}

// re-export to satisfy "unused import" in case GenericResult isn't used
export type { GenericResult };
